import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  ChevronRight,
  Edit3,
  Image as ImageIcon,
  ImageOff,
  Loader2,
  Package,
  Plus,
  Power,
  PowerOff,
  RefreshCw,
  Star,
  Tags,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "react-toastify";
import { catalogApi } from "../../api/catalogApi";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import MetaCatalogConnectionPanel from "./MetaCatalogConnectionPanel";

const PAGE_SIZE = 25;
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const MANAGED_IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const LEGACY_CATEGORY_VALUE = "__legacy_category__";

const emptyPage = { items: [], page: 1, pageSize: PAGE_SIZE, totalCount: 0 };
const emptyMetaSync = {
  connectionStatus: "not_connected",
  reconnectRequired: true,
  items: [],
  loadError: "",
};

const catalogDefaults = {
  name: "",
  description: "",
  defaultCurrency: "INR",
  isActive: true,
};

const variantDefaults = {
  name: "Default",
  sku: "",
  unitCode: "",
  unitLabel: "",
  packSize: "",
  minimumQuantity: "1",
  quantityStep: "1",
  price: "",
  currency: "",
  availabilityStatus: "available",
  availableQuantity: "",
  isActive: true,
  makeDefault: false,
};

const itemDefaults = {
  kind: "product",
  name: "",
  description: "",
  categoryId: "",
  category: null,
  brand: "",
  isActive: true,
  defaultVariant: { ...variantDefaults, makeDefault: true },
};

const fieldClass =
  "mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100";
const labelClass = "text-xs font-semibold text-slate-700";

const nullableNumber = value =>
  value === "" || value === null || value === undefined ? null : Number(value);

const optionalText = value => {
  const trimmed = String(value ?? "").trim();
  return trimmed || null;
};

const variantPayload = values => {
  const price = nullableNumber(values.price);
  return {
    name: values.name.trim(),
    sku: optionalText(values.sku),
    unitCode: optionalText(values.unitCode),
    unitLabel: optionalText(values.unitLabel),
    packSize: nullableNumber(values.packSize),
    minimumQuantity: Number(values.minimumQuantity),
    quantityStep: Number(values.quantityStep),
    price,
    currency: price === null ? null : optionalText(values.currency)?.toUpperCase(),
    availabilityStatus: values.availabilityStatus,
    availableQuantity: nullableNumber(values.availableQuantity),
    isActive: Boolean(values.isActive),
    makeDefault: Boolean(values.makeDefault),
  };
};

const fromVariant = variant => ({
  ...variantDefaults,
  ...variant,
  sku: variant?.sku ?? "",
  unitCode: variant?.unitCode ?? "",
  unitLabel: variant?.unitLabel ?? "",
  packSize: variant?.packSize ?? "",
  minimumQuantity: variant?.minimumQuantity ?? "1",
  quantityStep: variant?.quantityStep ?? "1",
  price: variant?.price ?? "",
  currency: variant?.currency ?? "",
  availableQuantity: variant?.availableQuantity ?? "",
  makeDefault: false,
});

function Modal({
  title,
  children,
  onClose,
  footer,
  width = "max-w-2xl",
  closeDisabled = false,
}) {
  useEffect(() => {
    const onKeyDown = event => {
      if (event.key === "Escape" && !closeDisabled) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [closeDisabled, onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 p-4"
      role="presentation"
      onMouseDown={event => {
        if (event.target === event.currentTarget && !closeDisabled) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`max-h-[92vh] w-full ${width} overflow-hidden rounded-lg bg-white shadow-2xl`}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            className="rounded p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close"
            title="Close"
          >
            <X size={18} />
          </button>
        </header>
        <div className="max-h-[calc(92vh-130px)] overflow-y-auto px-5 py-4">
          {children}
        </div>
        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
            {footer}
          </footer>
        )}
      </section>
    </div>
  );
}

function ConflictNotice({ onReload }) {
  return (
    <div
      role="alert"
      className="mb-4 flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-950"
    >
      <div className="flex gap-2">
        <AlertTriangle size={18} className="mt-0.5 shrink-0" />
        <span>Catalogue data changed since you opened this form.</span>
      </div>
      <button
        type="button"
        onClick={onReload}
        className="inline-flex shrink-0 items-center gap-1 font-semibold text-amber-950 hover:underline"
      >
        <RefreshCw size={14} /> Reload
      </button>
    </div>
  );
}

function TextField({ label, value, onChange, required, type = "text", ...props }) {
  return (
    <label className={labelClass}>
      {label}
      <input
        {...props}
        type={type}
        value={value}
        required={required}
        onChange={event => onChange(event.target.value)}
        className={fieldClass}
      />
    </label>
  );
}

function VariantFields({ values, onChange, defaultCurrency }) {
  const set = (key, value) => onChange({ ...values, [key]: value });
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <TextField
        label="Variant name"
        value={values.name}
        onChange={value => set("name", value)}
        required
        maxLength={250}
      />
      <TextField
        label="SKU"
        value={values.sku}
        onChange={value => set("sku", value)}
        maxLength={100}
      />
      <TextField
        label="Unit code"
        value={values.unitCode}
        onChange={value => set("unitCode", value)}
        maxLength={32}
        placeholder="piece"
      />
      <TextField
        label="Unit label"
        value={values.unitLabel}
        onChange={value => set("unitLabel", value)}
        maxLength={80}
        placeholder="Piece"
      />
      <TextField
        label="Pack size"
        type="number"
        min="0"
        step="any"
        value={values.packSize}
        onChange={value => set("packSize", value)}
      />
      <TextField
        label="Minimum quantity"
        type="number"
        min="0.000001"
        step="any"
        required
        value={values.minimumQuantity}
        onChange={value => set("minimumQuantity", value)}
      />
      <TextField
        label="Quantity step"
        type="number"
        min="0.000001"
        step="any"
        required
        value={values.quantityStep}
        onChange={value => set("quantityStep", value)}
      />
      <TextField
        label="Price"
        type="number"
        min="0"
        step="0.01"
        value={values.price}
        onChange={value => {
          const next = { ...values, price: value };
          if (value && !next.currency) next.currency = defaultCurrency || "";
          if (!value) next.currency = "";
          onChange(next);
        }}
      />
      <TextField
        label="Currency"
        value={values.currency}
        onChange={value => set("currency", value.toUpperCase())}
        required={values.price !== ""}
        disabled={values.price === ""}
        maxLength={3}
        minLength={3}
        placeholder={defaultCurrency || "INR"}
      />
      <label className={labelClass}>
        Availability
        <select
          value={values.availabilityStatus}
          onChange={event => set("availabilityStatus", event.target.value)}
          className={fieldClass}
        >
          <option value="available">Available</option>
          <option value="unavailable">Unavailable</option>
          <option value="madeToOrder">Made to order</option>
          <option value="enquiryOnly">Enquiry only</option>
        </select>
      </label>
      <TextField
        label="Available quantity"
        type="number"
        min="0"
        step="any"
        value={values.availableQuantity}
        onChange={value => set("availableQuantity", value)}
      />
      <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
        <input
          type="checkbox"
          checked={values.isActive}
          onChange={event => set("isActive", event.target.checked)}
          className="h-4 w-4 accent-emerald-700"
        />
        Active
      </label>
    </div>
  );
}

function CatalogForm({ initial, conflict, onReload, onCancel, onSubmit, saving }) {
  const [values, setValues] = useState(() => ({
    ...catalogDefaults,
    ...(initial || {}),
  }));
  return (
    <form
      aria-label="Catalogue form"
      onSubmit={event => {
        event.preventDefault();
        onSubmit(values);
      }}
    >
      {conflict && <ConflictNotice onReload={onReload} />}
      <div className="grid gap-4">
        <TextField
          label="Catalogue name"
          value={values.name}
          onChange={name => setValues(current => ({ ...current, name }))}
          required
          maxLength={200}
          autoFocus
        />
        <label className={labelClass}>
          Description
          <textarea
            value={values.description ?? ""}
            onChange={event =>
              setValues(current => ({ ...current, description: event.target.value }))
            }
            maxLength={2000}
            rows={3}
            className={fieldClass}
          />
        </label>
        <TextField
          label="Default currency"
          value={values.defaultCurrency}
          onChange={defaultCurrency =>
            setValues(current => ({
              ...current,
              defaultCurrency: defaultCurrency.toUpperCase(),
            }))
          }
          required
          minLength={3}
          maxLength={3}
        />
        {!initial && (
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={event =>
                setValues(current => ({ ...current, isActive: event.target.checked }))
              }
              className="h-4 w-4 accent-emerald-700"
            />
            Active
          </label>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md border px-4 py-2 text-sm">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {saving && <Loader2 size={15} className="animate-spin" />}
          Save
        </button>
      </div>
    </form>
  );
}

function ItemForm({
  initial,
  categories,
  categoriesLoading,
  categoriesError,
  defaultCurrency,
  conflict,
  onReload,
  onCancel,
  onSubmit,
  saving,
}) {
  const [values, setValues] = useState(() =>
    initial
      ? {
          ...itemDefaults,
          ...initial,
          categoryId: initial.categoryId || "",
          category: initial.category ?? "",
          brand: initial.brand ?? "",
          description: initial.description ?? "",
        }
      : itemDefaults,
  );
  const hasLegacyCategory = Boolean(values.category && !values.categoryId);
  const assignedCategory = values.categoryId
    ? categories.find(category => category.id === values.categoryId)
    : null;
  const activeCategories = categories.filter(category => category.isActive);
  const categoryValue = hasLegacyCategory ? LEGACY_CATEGORY_VALUE : values.categoryId;
  const showCategoryHelp = Boolean(
    categoriesLoading ||
    categoriesError ||
    hasLegacyCategory ||
    (assignedCategory && !assignedCategory.isActive),
  );

  return (
    <form
      onSubmit={event => {
        event.preventDefault();
        onSubmit(values);
      }}
    >
      {conflict && <ConflictNotice onReload={onReload} />}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className={labelClass}>
          Item kind
          <select
            value={values.kind}
            onChange={event =>
              setValues(current => ({ ...current, kind: event.target.value }))
            }
            className={fieldClass}
          >
            <option value="product">Product</option>
            <option value="service">Service</option>
          </select>
        </label>
        <TextField
          label="Item name"
          value={values.name}
          onChange={name => setValues(current => ({ ...current, name }))}
          required
          maxLength={250}
          autoFocus
        />
        <label className={labelClass}>
          Category
          <select
            aria-label="Category"
            aria-describedby={showCategoryHelp ? "item-category-help" : undefined}
            value={categoryValue}
            disabled={categoriesLoading}
            onChange={event => {
              const categoryId = event.target.value;
              setValues(current => ({
                ...current,
                categoryId,
                category: null,
              }));
            }}
            className={fieldClass}
          >
            <option value="">No category</option>
            {hasLegacyCategory && (
              <option value={LEGACY_CATEGORY_VALUE} disabled>
                Legacy: {values.category} (unmapped)
              </option>
            )}
            {values.categoryId && !assignedCategory && (
              <option value={values.categoryId} disabled>
                Current: {values.category || "Unavailable category"}
              </option>
            )}
            {assignedCategory && !assignedCategory.isActive && (
              <option value={assignedCategory.id} disabled>
                {assignedCategory.name} (inactive - current)
              </option>
            )}
            {activeCategories.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          {showCategoryHelp && (
            <span id="item-category-help" className="mt-1 block text-xs font-normal text-slate-500">
              {categoriesLoading
                ? "Loading categories..."
                : categoriesError ||
                  (hasLegacyCategory
                    ? "This legacy value is not linked to a managed category. Choose a category or No category to replace it."
                    : "This category is inactive. Choose an active category or No category to change it.")}
            </span>
          )}
        </label>
        <TextField
          label="Brand"
          value={values.brand}
          onChange={brand => setValues(current => ({ ...current, brand }))}
          maxLength={100}
        />
      </div>
      <label className={`${labelClass} mt-4 block`}>
        Description
        <textarea
          value={values.description}
          onChange={event =>
            setValues(current => ({ ...current, description: event.target.value }))
          }
          maxLength={4000}
          rows={3}
          className={fieldClass}
        />
      </label>
      {!initial && (
        <section className="mt-5 border-t border-slate-200 pt-5">
          <h3 className="mb-4 text-sm font-semibold text-slate-900">Default variant</h3>
          <VariantFields
            values={values.defaultVariant}
            defaultCurrency={defaultCurrency}
            onChange={defaultVariant =>
              setValues(current => ({ ...current, defaultVariant }))
            }
          />
        </section>
      )}
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md border px-4 py-2 text-sm">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {saving && <Loader2 size={15} className="animate-spin" />}
          Save
        </button>
      </div>
    </form>
  );
}

function CategoryManager({
  catalog,
  categories,
  loading,
  loadError,
  canCreate,
  canDelete,
  onRefresh,
  onClose,
}) {
  const [editor, setEditor] = useState(null);
  const [pendingDeactivate, setPendingDeactivate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);

  const openEditor = category => {
    setError("");
    setConflict(false);
    setEditor({
      category,
      name: category?.name ?? "",
      sortOrder: category?.sortOrder ?? 0,
    });
  };

  const refresh = async () => {
    setConflict(false);
    setError("");
    await onRefresh();
  };

  const handleError = requestError => {
    setError(requestError.message);
    setConflict(requestError.status === 409);
  };

  const saveCategory = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const payload = {
        name: editor.name.trim(),
        sortOrder: Number(editor.sortOrder),
      };
      if (editor.category) {
        await catalogApi.updateCategory(catalog.id, editor.category.id, {
          ...payload,
          expectedVersion: editor.category.version,
        });
      } else {
        await catalogApi.createCategory(catalog.id, {
          ...payload,
          isActive: true,
        });
      }
      setEditor(null);
      await onRefresh();
      toast.success("Category saved.");
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setBusy(false);
    }
  };

  const activateCategory = async category => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await catalogApi.activateCategory(catalog.id, category.id, category.version);
      await onRefresh();
      toast.success("Category activated.");
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setBusy(false);
    }
  };

  const deactivateCategory = async () => {
    if (!pendingDeactivate || busy) return;
    setBusy(true);
    setError("");
    try {
      await catalogApi.deactivateCategory(
        catalog.id,
        pendingDeactivate.id,
        pendingDeactivate.version,
      );
      setPendingDeactivate(null);
      await onRefresh();
      toast.success("Category deactivated.");
    } catch (requestError) {
      handleError(requestError);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Modal
        title={`Categories · ${catalog.name}`}
        onClose={onClose}
        width="max-w-xl"
        closeDisabled={busy}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <span className="text-xs text-slate-500">{categories.length} total</span>
          {canCreate && !editor && (
            <button
              type="button"
              onClick={() => openEditor(null)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white"
            >
              <Plus size={15} /> Add category
            </button>
          )}
        </div>

        {(error || loadError) && (
          <div role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            <div className="flex items-start justify-between gap-3">
              <span>{error || loadError}</span>
              {conflict && (
                <button type="button" onClick={refresh} className="shrink-0 font-semibold hover:underline">
                  Reload
                </button>
              )}
            </div>
          </div>
        )}

        {editor && (
          <form aria-label="Category form" onSubmit={saveCategory} className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="grid grid-cols-[minmax(0,1fr)_110px] gap-3">
              <TextField
                label="Category name"
                value={editor.name}
                onChange={name => setEditor(current => ({ ...current, name }))}
                required
                maxLength={100}
                autoFocus
              />
              <TextField
                label="Sort order"
                type="number"
                min="0"
                step="1"
                value={editor.sortOrder}
                onChange={sortOrder => setEditor(current => ({ ...current, sortOrder }))}
                required
              />
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" disabled={busy} onClick={() => setEditor(null)} className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm">
                Cancel
              </button>
              <button type="submit" disabled={busy || !editor.name.trim()} className="inline-flex items-center gap-1.5 rounded bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50">
                {busy && <Loader2 size={14} className="animate-spin" />}
                Save category
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div role="status" aria-label="Loading categories" className="flex justify-center py-10 text-slate-500">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : categories.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-500">
            No categories in this catalogue.
          </div>
        ) : (
          <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
            {categories.map(category => (
              <div key={category.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold text-slate-900">{category.name}</span>
                    <StatusBadge active={category.isActive} />
                  </div>
                  <span className="text-xs text-slate-500">Sort order {category.sortOrder}</span>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {canCreate && (
                    <>
                      <button type="button" disabled={busy} onClick={() => openEditor(category)} className="rounded p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-40" aria-label={`Edit category ${category.name}`} title="Edit">
                        <Edit3 size={15} />
                      </button>
                      {!category.isActive && (
                        <button type="button" disabled={busy} onClick={() => activateCategory(category)} className="rounded p-1.5 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40" aria-label={`Activate category ${category.name}`} title="Activate">
                          <Power size={15} />
                        </button>
                      )}
                    </>
                  )}
                  {canDelete && category.isActive && (
                    <button type="button" disabled={busy} onClick={() => setPendingDeactivate(category)} className="rounded p-1.5 text-red-700 hover:bg-red-50 disabled:opacity-40" aria-label={`Deactivate category ${category.name}`} title="Deactivate">
                      <PowerOff size={15} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {pendingDeactivate && (
        <Modal
          title="Deactivate category?"
          onClose={() => !busy && setPendingDeactivate(null)}
          width="max-w-md"
          closeDisabled={busy}
          footer={
            <>
              <button type="button" disabled={busy} onClick={() => setPendingDeactivate(null)} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm">
                Cancel
              </button>
              <button type="button" disabled={busy} onClick={deactivateCategory} className="inline-flex items-center gap-2 rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
                {busy && <Loader2 size={15} className="animate-spin" />}
                Deactivate
              </button>
            </>
          }
        >
          <p className="text-sm text-slate-700">
            <strong>{pendingDeactivate.name}</strong> will no longer be available for new item assignments.
          </p>
        </Modal>
      )}
    </>
  );
}

function VariantForm({
  initial,
  defaultCurrency,
  conflict,
  onReload,
  onCancel,
  onSubmit,
  saving,
}) {
  const [values, setValues] = useState(() =>
    initial ? fromVariant(initial) : variantDefaults,
  );
  return (
    <form
      onSubmit={event => {
        event.preventDefault();
        onSubmit(values);
      }}
    >
      {conflict && <ConflictNotice onReload={onReload} />}
      <VariantFields values={values} onChange={setValues} defaultCurrency={defaultCurrency} />
      <div className="mt-6 flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-md border px-4 py-2 text-sm">
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {saving && <Loader2 size={15} className="animate-spin" />}
          Save
        </button>
      </div>
    </form>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
        active ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function Pagination({ pageData, onPage }) {
  const totalPages = Math.max(1, Math.ceil(pageData.totalCount / pageData.pageSize));
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-4 py-3 text-sm">
      <button
        type="button"
        disabled={pageData.page <= 1}
        onClick={() => onPage(pageData.page - 1)}
        className="rounded border px-3 py-1.5 disabled:opacity-40"
      >
        Previous
      </button>
      <span className="text-slate-600">
        {pageData.page} / {totalPages}
      </span>
      <button
        type="button"
        disabled={pageData.page >= totalPages}
        onClick={() => onPage(pageData.page + 1)}
        className="rounded border px-3 py-1.5 disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}

function ImagePreview({ image }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="flex h-20 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-slate-100">
      {broken ? (
        <ImageOff size={22} className="text-slate-400" aria-label="Image unavailable" />
      ) : (
        <img
          src={image.imageUrl}
          alt={image.altText || ""}
          onError={() => setBroken(true)}
          className="h-full w-full object-cover"
        />
      )}
    </div>
  );
}

function CatalogAssetsManager({
  panel,
  catalogId,
  itemId,
  canCreate,
  canDelete,
  onClose,
}) {
  const isVariant = panel.scope === "variant";
  const variantId = panel.variant?.id;
  const isAlias = panel.kind === "alias";
  const scopeLabel = isVariant ? `Variant · ${panel.variant.name}` : "Item";
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [alias, setAlias] = useState("");
  const [imageMode, setImageMode] = useState("upload");
  const [upload, setUpload] = useState({
    file: null,
    altText: "",
    sortOrder: "0",
    isPrimary: false,
  });
  const [previewUrl, setPreviewUrl] = useState("");
  const [image, setImage] = useState({
    imageUrl: "",
    altText: "",
    sortOrder: "0",
    isPrimary: false,
  });
  const [removeTarget, setRemoveTarget] = useState(null);

  useEffect(() => {
    if (!upload.file || typeof URL.createObjectURL !== "function") {
      setPreviewUrl("");
      return undefined;
    }
    const objectUrl = URL.createObjectURL(upload.file);
    setPreviewUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [upload.file]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      let result;
      if (isAlias && isVariant) {
        result = await catalogApi.listAllVariantAliases(catalogId, itemId, variantId);
      } else if (isAlias) {
        result = await catalogApi.listAllItemAliases(catalogId, itemId);
      } else if (isVariant) {
        result = await catalogApi.listAllVariantImages(catalogId, itemId, variantId);
      } else {
        result = await catalogApi.listAllItemImages(catalogId, itemId);
      }
      setEntries(result);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(false);
    }
  }, [catalogId, isAlias, isVariant, itemId, variantId]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const addAlias = async event => {
    event.preventDefault();
    const normalizedForRequest = alias.trim().replace(/\s+/g, " ");
    if (!normalizedForRequest) return;
    setSaving(true);
    setError("");
    try {
      if (isVariant) {
        await catalogApi.addVariantAlias(
          catalogId,
          itemId,
          variantId,
          normalizedForRequest,
        );
      } else {
        await catalogApi.addItemAlias(catalogId, itemId, normalizedForRequest);
      }
      setAlias("");
      await loadEntries();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const addImage = async event => {
    event.preventDefault();
    const imageUrl = image.imageUrl.trim();
    if (!/^https:\/\//i.test(imageUrl)) {
      setError("Use an HTTPS image URL.");
      return;
    }
    setSaving(true);
    setError("");
    const payload = {
      imageUrl,
      altText: optionalText(image.altText),
      sortOrder: Number(image.sortOrder || 0),
      isPrimary: image.isPrimary,
    };
    try {
      if (isVariant) {
        await catalogApi.addVariantImage(catalogId, itemId, variantId, payload);
      } else {
        await catalogApi.addItemImage(catalogId, itemId, payload);
      }
      setImage({ imageUrl: "", altText: "", sortOrder: "0", isPrimary: false });
      await loadEntries();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async event => {
    event.preventDefault();
    if (!upload.file) {
      setError("Choose an image to upload.");
      return;
    }
    if (!MANAGED_IMAGE_TYPES.has(upload.file.type)) {
      setError("Choose a JPEG, PNG, or WebP image.");
      return;
    }
    if (upload.file.size > MAX_IMAGE_SIZE_BYTES) {
      setError("Image files cannot exceed 5 MB.");
      return;
    }

    setSaving(true);
    setError("");
    const payload = {
      file: upload.file,
      altText: optionalText(upload.altText),
      sortOrder: Number(upload.sortOrder || 0),
      isPrimary: upload.isPrimary,
    };
    try {
      const created = isVariant
        ? await catalogApi.uploadVariantImage(
            catalogId,
            itemId,
            variantId,
            payload,
          )
        : await catalogApi.uploadItemImage(catalogId, itemId, payload);
      setEntries(current => [
        created,
        ...current.filter(entry => entry.id !== created.id),
      ]);
      setUpload({
        file: null,
        altText: "",
        sortOrder: "0",
        isPrimary: false,
      });
      await loadEntries();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const setPrimary = async entry => {
    setSaving(true);
    setError("");
    try {
      if (isVariant) {
        await catalogApi.setPrimaryVariantImage(
          catalogId,
          itemId,
          variantId,
          entry.id,
        );
      } else {
        await catalogApi.setPrimaryItemImage(catalogId, itemId, entry.id);
      }
      await loadEntries();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async () => {
    setSaving(true);
    setError("");
    try {
      if (isAlias && isVariant) {
        await catalogApi.removeVariantAlias(
          catalogId,
          itemId,
          variantId,
          removeTarget.id,
        );
      } else if (isAlias) {
        await catalogApi.removeItemAlias(catalogId, itemId, removeTarget.id);
      } else if (isVariant) {
        await catalogApi.removeVariantImage(
          catalogId,
          itemId,
          variantId,
          removeTarget.id,
        );
      } else {
        await catalogApi.removeItemImage(catalogId, itemId, removeTarget.id);
      }
      setRemoveTarget(null);
      await loadEntries();
    } catch (requestError) {
      setRemoveTarget(null);
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Modal
        title={`${scopeLabel} ${isAlias ? "aliases" : "images"}`}
        onClose={saving ? () => {} : onClose}
        closeDisabled={saving}
        width={isAlias ? "max-w-xl" : "max-w-3xl"}
      >
        <p className="mb-4 text-xs font-semibold uppercase text-slate-500">
          {isVariant ? "Variant-level" : "Item-level"} {isAlias ? "aliases" : "images"}
        </p>
        {error && (
          <div role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        {canCreate && isAlias && (
          <form onSubmit={addAlias} className="mb-5 flex gap-2" aria-label="Add alias">
            <input
              value={alias}
              onChange={event => setAlias(event.target.value)}
              maxLength={200}
              required
              placeholder="Add a search alias"
              aria-label="Alias"
              className={`${fieldClass} mt-0`}
            />
            <button
              type="submit"
              disabled={saving}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
            >
              <Plus size={15} /> Add
            </button>
          </form>
        )}

        {canCreate && !isAlias && (
          <div className="mb-4 flex items-center gap-2" role="tablist" aria-label="Image source">
            <button
              type="button"
              role="tab"
              aria-selected={imageMode === "upload"}
              onClick={() => {
                setImageMode("upload");
                setError("");
              }}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold ${
                imageMode === "upload"
                  ? "bg-emerald-700 text-white"
                  : "border border-slate-300 bg-white text-slate-700"
              }`}
            >
              <Upload size={15} /> Upload Image
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={imageMode === "url"}
              onClick={() => {
                setImageMode("url");
                setError("");
              }}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${
                imageMode === "url"
                  ? "bg-emerald-700 text-white"
                  : "border border-slate-300 bg-white text-slate-700"
              }`}
            >
              Add by URL
            </button>
          </div>
        )}

        {canCreate && !isAlias && imageMode === "upload" && (
          <form
            onSubmit={uploadImage}
            className="mb-5 grid grid-cols-1 gap-3 border-b border-slate-200 pb-5 sm:grid-cols-2"
            aria-label="Upload image"
          >
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Image
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  required
                  disabled={saving}
                  onChange={event =>
                    setUpload(current => ({
                      ...current,
                      file: event.target.files?.[0] ?? null,
                    }))
                  }
                  className={`${fieldClass} block`}
                  aria-label="Choose image"
                />
              </label>
              <p className="mt-1 text-xs text-slate-500">
                JPEG, PNG, or WebP. Maximum 5 MB.
              </p>
            </div>
            {upload.file && (
              <div className="sm:col-span-2 flex items-center gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Selected upload preview"
                    className="h-20 w-24 rounded-md border border-slate-200 object-cover"
                  />
                ) : (
                  <div className="flex h-20 w-24 items-center justify-center rounded-md border border-slate-200 bg-white">
                    <ImageIcon size={22} className="text-slate-400" />
                  </div>
                )}
                <p className="min-w-0 truncate text-sm font-medium text-slate-800">
                  {upload.file.name}
                </p>
              </div>
            )}
            <TextField
              label="Alt text"
              value={upload.altText}
              onChange={altText => setUpload(current => ({ ...current, altText }))}
              maxLength={300}
            />
            <TextField
              label="Sort order"
              value={upload.sortOrder}
              onChange={sortOrder => setUpload(current => ({ ...current, sortOrder }))}
              type="number"
              min="0"
              step="1"
              required
            />
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={upload.isPrimary}
                onChange={event =>
                  setUpload(current => ({
                    ...current,
                    isPrimary: event.target.checked,
                  }))
                }
                className="h-4 w-4 accent-emerald-700"
              />
              Set as primary
            </label>
            <div className="flex justify-end sm:items-end">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                {saving ? (
                  <>
                    <Loader2 size={15} className="animate-spin" /> Uploading image…
                  </>
                ) : (
                  <>
                    <Upload size={15} /> Upload Image
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {canCreate && !isAlias && imageMode === "url" && (
          <form
            onSubmit={addImage}
            className="mb-5 grid grid-cols-1 gap-3 border-b border-slate-200 pb-5 sm:grid-cols-2"
            aria-label="Add image by URL"
          >
            <div className="sm:col-span-2">
              <TextField
                label="HTTPS image URL"
                value={image.imageUrl}
                onChange={imageUrl => setImage(current => ({ ...current, imageUrl }))}
                required
                type="url"
                maxLength={2048}
                placeholder="https://"
              />
            </div>
            <TextField
              label="Alt text"
              value={image.altText}
              onChange={altText => setImage(current => ({ ...current, altText }))}
              maxLength={300}
            />
            <TextField
              label="Sort order"
              value={image.sortOrder}
              onChange={sortOrder => setImage(current => ({ ...current, sortOrder }))}
              type="number"
              min="0"
              step="1"
              required
            />
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={image.isPrimary}
                onChange={event =>
                  setImage(current => ({ ...current, isPrimary: event.target.checked }))
                }
                className="h-4 w-4 accent-emerald-700"
              />
              Set as primary
            </label>
            <div className="flex justify-end sm:items-end">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                <Plus size={15} /> Add image
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-500">
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-500">
            No {isAlias ? "aliases" : "images"} added.
          </div>
        ) : isAlias ? (
          <div className="flex flex-wrap gap-2">
            {entries.map(entry => (
              <span
                key={entry.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-50 py-1 pl-3 pr-1.5 text-sm text-slate-800"
              >
                {entry.alias}
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => setRemoveTarget(entry)}
                    className="rounded-full p-1 text-slate-500 hover:bg-red-100 hover:text-red-700"
                    aria-label={`Delete alias ${entry.alias}`}
                    title="Delete alias"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </span>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {entries
              .slice()
              .sort((left, right) => left.sortOrder - right.sortOrder)
              .map(entry => (
                <div
                  key={entry.id}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-slate-200 p-3"
                >
                  <ImagePreview image={entry} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900">
                        {entry.altText || "Catalogue image"}
                      </span>
                      {entry.isPrimary && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          Primary
                        </span>
                      )}
                    </div>
                    <p className="mt-1 truncate text-xs text-slate-500">
                      Sort order {entry.sortOrder}
                      {entry.storageProvider && (
                        <>
                          {" · "}
                          {String(entry.storageProvider).toLowerCase() === "r2"
                            ? "Uploaded"
                            : "External URL"}
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-1">
                    {canCreate && !entry.isPrimary && (
                      <button
                        type="button"
                        onClick={() => setPrimary(entry)}
                        disabled={saving}
                        className="rounded p-1.5 text-amber-700 hover:bg-amber-50"
                        aria-label={`Set ${entry.altText || "image"} as primary`}
                        title="Set primary"
                      >
                        <Star size={16} />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => setRemoveTarget(entry)}
                        className="rounded p-1.5 text-red-700 hover:bg-red-50"
                        aria-label={`Delete ${entry.altText || "image"}`}
                        title="Delete image"
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}
      </Modal>

      {removeTarget && (
        <Modal
          title={`Delete ${isAlias ? "alias" : "image"}?`}
          onClose={() => setRemoveTarget(null)}
          width="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setRemoveTarget(null)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={removeEntry}
                disabled={saving}
                className="rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-60"
              >
                Delete
              </button>
            </>
          }
        >
          <p className="text-sm text-slate-700">
            This {isAlias ? "alias" : "image"} will be removed from the catalogue.
          </p>
        </Modal>
      )}
    </>
  );
}

export default function CatalogManagementPage() {
  const { can, hasAllAccess, isPlatformSuperAdmin } = useAuth();
  const allowed = useCallback(
    code => Boolean(hasAllAccess || isPlatformSuperAdmin || can?.(code)),
    [can, hasAllAccess, isPlatformSuperAdmin],
  );
  const canCreate = allowed(FK.CATALOG_CREATE);
  const canDelete = allowed(FK.CATALOG_DELETE);

  const [catalogs, setCatalogs] = useState(emptyPage);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState(emptyPage);
  const [variants, setVariants] = useState(emptyPage);
  const [metaSync, setMetaSync] = useState(emptyMetaSync);
  const [syncingVariantId, setSyncingVariantId] = useState(null);
  const [selectedCatalogId, setSelectedCatalogId] = useState(null);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const selectedCatalogIdRef = useRef(null);
  const selectedItemIdRef = useRef(null);
  const categoriesRequestRef = useRef(0);
  const [loading, setLoading] = useState({
    catalogs: true,
    categories: false,
    items: false,
    variants: false,
  });
  const [error, setError] = useState("");
  const [categoryError, setCategoryError] = useState("");
  const [form, setForm] = useState(null);
  const [categoryPanel, setCategoryPanel] = useState(false);
  const [assetPanel, setAssetPanel] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);

  const selectedCatalog = useMemo(
    () => catalogs.items.find(entry => entry.id === selectedCatalogId) ?? null,
    [catalogs.items, selectedCatalogId],
  );
  const selectedItem = useMemo(
    () => items.items.find(entry => entry.id === selectedItemId) ?? null,
    [items.items, selectedItemId],
  );

  const selectCatalog = useCallback(catalogId => {
    const nextCatalogId = catalogId || null;
    setAssetPanel(null);
    setCategoryPanel(false);
    setCategories([]);
    setCategoryError("");
    selectedCatalogIdRef.current = nextCatalogId;
    selectedItemIdRef.current = null;
    setSelectedCatalogId(nextCatalogId);
    setSelectedItemId(null);
    setItems(emptyPage);
    setVariants(emptyPage);
    setMetaSync(emptyMetaSync);
  }, []);

  const selectItem = useCallback(itemId => {
    const nextItemId = itemId || null;
    setAssetPanel(null);
    selectedItemIdRef.current = nextItemId;
    setSelectedItemId(nextItemId);
    setVariants(emptyPage);
    setMetaSync(emptyMetaSync);
  }, []);

  const loadCatalogs = useCallback(async (page = 1, preferredId = null) => {
    setLoading(current => ({ ...current, catalogs: true }));
    setError("");
    try {
      const result = await catalogApi.listCatalogs({ page, pageSize: PAGE_SIZE });
      setCatalogs(result);
      const candidate = preferredId || selectedCatalogIdRef.current;
      const nextCatalogId = result.items.some(entry => entry.id === candidate)
        ? candidate
        : result.items.find(entry => entry.isDefault)?.id ?? result.items[0]?.id ?? null;
      if (nextCatalogId !== selectedCatalogIdRef.current) {
        selectCatalog(nextCatalogId);
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setLoading(current => ({ ...current, catalogs: false }));
    }
  }, [selectCatalog]);

  const loadCategories = useCallback(async catalogId => {
    const requestId = categoriesRequestRef.current + 1;
    categoriesRequestRef.current = requestId;
    if (!catalogId) {
      setCategories([]);
      setCategoryError("");
      setLoading(current => ({ ...current, categories: false }));
      return;
    }
    setLoading(current => ({ ...current, categories: true }));
    setCategoryError("");
    try {
      const result = await catalogApi.listAllCategories(catalogId);
      if (selectedCatalogIdRef.current !== catalogId) return;
      setCategories(result);
    } catch (requestError) {
      if (selectedCatalogIdRef.current !== catalogId) return;
      setCategories([]);
      setCategoryError(requestError.message);
    } finally {
      if (categoriesRequestRef.current === requestId) {
        setLoading(current => ({ ...current, categories: false }));
      }
    }
  }, []);

  const loadItems = useCallback(async (catalogId, page = 1, preferredId = null) => {
    if (!catalogId) {
      setItems(emptyPage);
      selectItem(null);
      return;
    }
    setLoading(current => ({ ...current, items: true }));
    setError("");
    try {
      const result = await catalogApi.listItems(catalogId, {
        page,
        pageSize: PAGE_SIZE,
      });
      if (selectedCatalogIdRef.current !== catalogId) return;
      setItems(result);
      const candidate = preferredId || selectedItemIdRef.current;
      const nextItemId = result.items.some(entry => entry.id === candidate)
        ? candidate
        : result.items[0]?.id ?? null;
      if (nextItemId !== selectedItemIdRef.current) {
        selectItem(nextItemId);
      }
    } catch (requestError) {
      if (selectedCatalogIdRef.current !== catalogId) return;
      setItems(emptyPage);
      selectItem(null);
      setError(requestError.message);
    } finally {
      setLoading(current => ({ ...current, items: false }));
    }
  }, [selectItem]);

  const loadVariants = useCallback(async (catalogId, itemId, page = 1) => {
    if (!catalogId || !itemId) {
      setVariants(emptyPage);
      return;
    }
    setLoading(current => ({ ...current, variants: true }));
    setError("");
    try {
      const result = await catalogApi.listVariants(catalogId, itemId, {
        page,
        pageSize: PAGE_SIZE,
      });
      if (
        selectedCatalogIdRef.current !== catalogId ||
        selectedItemIdRef.current !== itemId
      ) {
        return;
      }
      setVariants(result);
      try {
        const status = await catalogApi.getMetaSyncStatuses(catalogId, itemId);
        if (
          selectedCatalogIdRef.current === catalogId &&
          selectedItemIdRef.current === itemId
        ) {
          setMetaSync({ ...status, loadError: "" });
        }
      } catch (requestError) {
        if (
          selectedCatalogIdRef.current === catalogId &&
          selectedItemIdRef.current === itemId
        ) {
          setMetaSync({ ...emptyMetaSync, loadError: requestError.message });
        }
      }
    } catch (requestError) {
      if (
        selectedCatalogIdRef.current !== catalogId ||
        selectedItemIdRef.current !== itemId
      ) {
        return;
      }
      setVariants(emptyPage);
      setError(requestError.message);
    } finally {
      setLoading(current => ({ ...current, variants: false }));
    }
  }, []);

  const syncVariant = async variant => {
    if (!selectedCatalogId || !selectedItemId || syncingVariantId) return;
    setSyncingVariantId(variant.id);
    try {
      const status = await catalogApi.syncVariantToMeta(
        selectedCatalogId,
        selectedItemId,
        variant.id,
      );
      setMetaSync(current => ({
        ...current,
        connectionStatus: "connected",
        reconnectRequired: false,
        loadError: "",
        items: [
          ...(current.items ?? []).filter(entry => entry.variantId !== variant.id),
          status,
        ],
      }));
      toast.success(`${variant.name} synced to Meta.`);
    } catch (requestError) {
      toast.error(requestError.message);
      await loadVariants(selectedCatalogId, selectedItemId, variants.page);
    } finally {
      setSyncingVariantId(null);
    }
  };

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  useEffect(() => {
    loadItems(selectedCatalogId);
  }, [loadItems, selectedCatalogId]);

  useEffect(() => {
    loadCategories(selectedCatalogId);
  }, [loadCategories, selectedCatalogId]);

  useEffect(() => {
    loadVariants(selectedCatalogId, selectedItemId);
  }, [loadVariants, selectedCatalogId, selectedItemId]);

  const closeForm = () => {
    setForm(null);
    setConflict(false);
  };

  const handleMutationError = requestError => {
    if (requestError.status === 409) {
      setConflict(true);
      return;
    }
    toast.error(requestError.message);
  };

  const reloadWorkspace = async () => {
    setConflict(false);
    await loadCatalogs(catalogs.page, selectedCatalogId);
    if (selectedCatalogId) {
      await loadCategories(selectedCatalogId);
      await loadItems(selectedCatalogId, items.page, selectedItemId);
      if (selectedItemId) await loadVariants(selectedCatalogId, selectedItemId, variants.page);
    }
  };

  const saveCatalog = async values => {
    setSaving(true);
    try {
      const payload = {
        name: values.name.trim(),
        description: optionalText(values.description),
        defaultCurrency: values.defaultCurrency.trim().toUpperCase(),
      };
      const saved = form.entity
        ? await catalogApi.updateCatalog(form.entity.id, {
            ...payload,
            expectedVersion: form.entity.version,
          })
        : await catalogApi.createCatalog({ ...payload, isActive: values.isActive });
      closeForm();
      await loadCatalogs(catalogs.page, saved.id);
      toast.success("Catalogue saved.");
    } catch (requestError) {
      handleMutationError(requestError);
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async values => {
    setSaving(true);
    try {
      const payload = {
        kind: values.kind,
        name: values.name.trim(),
        description: optionalText(values.description),
        categoryId:
          values.categoryId && values.categoryId !== LEGACY_CATEGORY_VALUE
            ? values.categoryId
            : null,
        category:
          values.categoryId === LEGACY_CATEGORY_VALUE ||
          (!values.categoryId && values.category)
            ? optionalText(values.category)
            : null,
        brand: optionalText(values.brand),
      };
      const saved = form.entity
        ? await catalogApi.updateItem(selectedCatalogId, form.entity.id, {
            ...payload,
            expectedVersion: form.entity.version,
          })
        : await catalogApi.createItem(selectedCatalogId, {
            ...payload,
            isActive: values.isActive,
            defaultVariant: variantPayload(values.defaultVariant),
          });
      closeForm();
      await loadItems(selectedCatalogId, items.page, saved.id);
      toast.success("Item saved.");
    } catch (requestError) {
      handleMutationError(requestError);
    } finally {
      setSaving(false);
    }
  };

  const saveVariant = async values => {
    setSaving(true);
    try {
      const payload = variantPayload(values);
      const saved = form.entity
        ? await catalogApi.updateVariant(
            selectedCatalogId,
            selectedItemId,
            form.entity.id,
            { ...payload, expectedVersion: form.entity.version },
          )
        : await catalogApi.createVariant(selectedCatalogId, selectedItemId, payload);
      closeForm();
      await loadVariants(selectedCatalogId, selectedItemId, variants.page);
      toast.success("Variant saved.");
      return saved;
    } catch (requestError) {
      handleMutationError(requestError);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const activate = async (kind, entity) => {
    try {
      if (kind === "catalog") {
        await catalogApi.activateCatalog(entity.id, entity.version);
        await loadCatalogs(catalogs.page, entity.id);
      } else if (kind === "item") {
        await catalogApi.activateItem(selectedCatalogId, entity.id, entity.version);
        await loadItems(selectedCatalogId, items.page, entity.id);
      } else {
        await catalogApi.activateVariant(
          selectedCatalogId,
          selectedItemId,
          entity.id,
          entity.version,
        );
        await loadVariants(selectedCatalogId, selectedItemId, variants.page);
      }
      toast.success("Activated.");
    } catch (requestError) {
      handleMutationError(requestError);
    }
  };

  const setDefault = async (kind, entity) => {
    try {
      if (kind === "catalog") {
        await catalogApi.setDefaultCatalog(entity.id, entity.version);
        await loadCatalogs(catalogs.page, entity.id);
      } else {
        await catalogApi.setDefaultVariant(
          selectedCatalogId,
          selectedItemId,
          entity.id,
          entity.version,
        );
        await loadVariants(selectedCatalogId, selectedItemId, variants.page);
      }
      toast.success("Default updated.");
    } catch (requestError) {
      handleMutationError(requestError);
    }
  };

  const openDeactivate = async (kind, entity) => {
    const requiresReplacement =
      entity.isDefault && (kind === "catalog" || kind === "variant");
    setConfirm({
      kind,
      entity,
      replacementId: "",
      options: [],
      loadingOptions: requiresReplacement,
      optionsError: "",
    });
    if (!requiresReplacement) return;

    try {
      const candidates =
        kind === "catalog"
          ? await catalogApi.listAllCatalogs({ activeOnly: true })
          : await catalogApi.listAllVariants(selectedCatalogId, selectedItemId, {
              activeOnly: true,
            });
      setConfirm(current =>
        current?.kind === kind && current?.entity.id === entity.id
          ? {
              ...current,
              options: candidates.filter(
                candidate => candidate.id !== entity.id && candidate.isActive,
              ),
              loadingOptions: false,
            }
          : current,
      );
    } catch (requestError) {
      setConfirm(current =>
        current?.kind === kind && current?.entity.id === entity.id
          ? {
              ...current,
              loadingOptions: false,
              optionsError: requestError.message,
            }
          : current,
      );
    }
  };

  const confirmDeactivate = async () => {
    const { kind, entity, replacementId } = confirm;
    setSaving(true);
    try {
      if (kind === "catalog") {
        const replacement = confirm.options.find(entry => entry.id === replacementId);
        await catalogApi.deactivateCatalog(
          entity.id,
          entity.version,
          replacement?.id ?? null,
          replacement?.version ?? null,
        );
        setConfirm(null);
        await loadCatalogs(catalogs.page);
      } else if (kind === "item") {
        await catalogApi.deactivateItem(selectedCatalogId, entity.id, entity.version);
        setConfirm(null);
        await loadItems(selectedCatalogId, items.page);
      } else {
        await catalogApi.deactivateVariant(
          selectedCatalogId,
          selectedItemId,
          entity.id,
          entity.version,
          replacementId || null,
        );
        setConfirm(null);
        await loadVariants(selectedCatalogId, selectedItemId, variants.page);
      }
      toast.success("Deactivated.");
    } catch (requestError) {
      setConfirm(null);
      handleMutationError(requestError);
    } finally {
      setSaving(false);
    }
  };

  const defaultVariant = variants.items.find(entry => entry.isDefault);

  return (
    <main className="min-h-full bg-slate-50 px-4 py-5 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-[1500px]">
        <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-slate-500">
              <Package size={18} />
              <span className="text-xs font-semibold uppercase">Commerce</span>
            </div>
            <h1 className="mt-1 text-2xl font-semibold text-slate-950">Catalogue</h1>
            {(hasAllAccess || (can?.(FK.SCHEDULING_VIEW) && can?.(FK.CATALOG_VIEW))) && <a href="/app/scheduling/setup" className="mt-2 inline-block text-sm font-semibold text-emerald-700 underline">Set up a bookable service</a>}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={reloadWorkspace}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
              aria-label="Refresh catalogue"
              title="Refresh"
            >
              <RefreshCw size={17} />
            </button>
            {canCreate && (
              <button
                type="button"
                onClick={() => {
                  setConflict(false);
                  setForm({ type: "catalog", entity: null });
                }}
                className="inline-flex items-center gap-2 rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-800"
              >
                <Plus size={17} /> New catalogue
              </button>
            )}
          </div>
        </header>

        <MetaCatalogConnectionPanel
          canConnect={canCreate}
          canDisconnect={canDelete}
        />

        {error && (
          <div role="alert" className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {conflict && !form && <ConflictNotice onReload={reloadWorkspace} />}

        <div className="grid min-h-[650px] grid-cols-1 overflow-hidden rounded-lg border border-slate-200 bg-white lg:grid-cols-[290px_1fr]">
          <aside className="border-b border-slate-200 lg:border-b-0 lg:border-r">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
              <h2 className="text-sm font-semibold text-slate-900">Catalogues</h2>
              <span className="text-xs text-slate-500">{catalogs.totalCount}</span>
            </div>
            {loading.catalogs ? (
              <div
                role="status"
                aria-label="Loading catalogues"
                className="flex items-center justify-center py-16 text-slate-500"
              >
                <Loader2 size={20} className="animate-spin" />
              </div>
            ) : catalogs.items.length === 0 ? (
              <div className="px-5 py-14 text-center text-sm text-slate-500">
                No catalogues yet.
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {catalogs.items.map(catalog => (
                  <button
                    key={catalog.id}
                    type="button"
                    onClick={() => selectCatalog(catalog.id)}
                    className={`w-full px-4 py-3 text-left hover:bg-slate-50 ${
                      catalog.id === selectedCatalogId
                        ? "border-l-2 border-emerald-700 bg-emerald-50/70"
                        : "border-l-2 border-transparent"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900">
                        {catalog.name}
                      </span>
                      {catalog.isDefault && (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                          <Star size={12} fill="currentColor" /> Default
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <StatusBadge active={catalog.isActive} />
                      <span className="text-xs text-slate-500">{catalog.defaultCurrency}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <Pagination pageData={catalogs} onPage={page => loadCatalogs(page)} />
          </aside>

          {!selectedCatalog ? (
            <div className="flex items-center justify-center p-10 text-sm text-slate-500">
              Select a catalogue to manage its items.
            </div>
          ) : (
            <div className="min-w-0">
              <section className="border-b border-slate-200 px-5 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-lg font-semibold text-slate-950">
                        {selectedCatalog.name}
                      </h2>
                      <StatusBadge active={selectedCatalog.isActive} />
                      {selectedCatalog.isDefault && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                          Default
                        </span>
                      )}
                    </div>
                    {selectedCatalog.description && (
                      <p className="mt-1 max-w-3xl text-sm text-slate-600">
                        {selectedCatalog.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => setCategoryPanel(true)}
                      className="inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm"
                    >
                      <Tags size={15} /> Manage categories
                    </button>
                    {canCreate && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setConflict(false);
                            setForm({ type: "catalog", entity: selectedCatalog });
                          }}
                          className="inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm"
                        >
                          <Edit3 size={15} /> Edit
                        </button>
                        {!selectedCatalog.isDefault && selectedCatalog.isActive && (
                          <button
                            type="button"
                            onClick={() => setDefault("catalog", selectedCatalog)}
                            className="inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm"
                          >
                            <Star size={15} /> Set default
                          </button>
                        )}
                        {!selectedCatalog.isActive && (
                          <button
                            type="button"
                            onClick={() => activate("catalog", selectedCatalog)}
                            className="inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm text-emerald-700"
                          >
                            <Power size={15} /> Activate
                          </button>
                        )}
                      </>
                    )}
                    {canDelete && selectedCatalog.isActive && (
                      <button
                        type="button"
                        onClick={() =>
                          openDeactivate("catalog", selectedCatalog)
                        }
                        className="inline-flex items-center gap-1.5 rounded border border-red-200 px-3 py-1.5 text-sm text-red-700"
                      >
                        <PowerOff size={15} /> Deactivate
                      </button>
                    )}
                  </div>
                </div>
              </section>

              <section className="border-b border-slate-200">
                <div className="flex items-center justify-between px-5 py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Items</h3>
                    <p className="text-xs text-slate-500">{items.totalCount} total</p>
                  </div>
                  {canCreate && selectedCatalog.isActive && (
                    <button
                      type="button"
                      onClick={() => {
                        setConflict(false);
                        setForm({ type: "item", entity: null });
                      }}
                      className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white"
                    >
                      <Plus size={15} /> Add item
                    </button>
                  )}
                </div>
                {loading.items ? (
                  <div className="flex justify-center py-12 text-slate-500">
                    <Loader2 size={20} className="animate-spin" />
                  </div>
                ) : items.items.length === 0 ? (
                  <div className="border-t border-slate-100 px-5 py-12 text-center text-sm text-slate-500">
                    No items in this catalogue.
                  </div>
                ) : (
                  <div className="overflow-x-auto border-t border-slate-100">
                    <table className="w-full min-w-[720px] text-left text-sm">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                          <th className="px-5 py-2.5 font-semibold">Name</th>
                          <th className="px-4 py-2.5 font-semibold">Kind</th>
                          <th className="px-4 py-2.5 font-semibold">Category</th>
                          <th className="px-4 py-2.5 font-semibold">Status</th>
                          <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.items.map(item => (
                          <tr
                            key={item.id}
                            className={item.id === selectedItemId ? "bg-emerald-50/50" : ""}
                          >
                            <td className="px-5 py-3">
                              <button
                                type="button"
                                onClick={() => selectItem(item.id)}
                                className="inline-flex items-center gap-1 font-semibold text-slate-900 hover:text-emerald-800"
                              >
                                {item.name} <ChevronRight size={14} />
                              </button>
                            </td>
                            <td className="px-4 py-3 capitalize text-slate-600">{item.kind}</td>
                            <td className="px-4 py-3 text-slate-600">
                              <span>{item.category || "—"}</span>
                              {item.category && !item.categoryId && (
                                <span className="ml-1.5 text-xs text-amber-700">Legacy</span>
                              )}
                              {item.categoryId &&
                                categories.find(category => category.id === item.categoryId)?.isActive === false && (
                                  <span className="ml-1.5 text-xs text-slate-500">Inactive</span>
                                )}
                            </td>
                            <td className="px-4 py-3"><StatusBadge active={item.isActive} /></td>
                            <td className="px-4 py-3">
                              <div className="flex justify-end gap-1">
                                {canCreate && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setConflict(false);
                                        setForm({ type: "item", entity: item });
                                      }}
                                      className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
                                      aria-label={`Edit ${item.name}`}
                                      title="Edit"
                                    >
                                      <Edit3 size={15} />
                                    </button>
                                    {!item.isActive && (
                                      <button
                                        type="button"
                                        onClick={() => activate("item", item)}
                                        className="rounded p-1.5 text-emerald-700 hover:bg-emerald-50"
                                        aria-label={`Activate ${item.name}`}
                                        title="Activate"
                                      >
                                        <Power size={15} />
                                      </button>
                                    )}
                                  </>
                                )}
                                {canDelete && item.isActive && (
                                  <button
                                    type="button"
                                    onClick={() => openDeactivate("item", item)}
                                    className="rounded p-1.5 text-red-700 hover:bg-red-50"
                                    aria-label={`Deactivate ${item.name}`}
                                    title="Deactivate"
                                  >
                                    <PowerOff size={15} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <Pagination pageData={items} onPage={page => loadItems(selectedCatalogId, page)} />
              </section>

              {selectedItem && (
                <section>
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">
                        Variants · {selectedItem.name}
                      </h3>
                      <p className="text-xs text-slate-500">
                        {defaultVariant
                          ? `Default: ${defaultVariant.name}`
                          : "No default variant is available"}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setAssetPanel({ scope: "item", kind: "alias" })}
                        className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
                      >
                        <Tags size={15} /> Item aliases
                      </button>
                      <button
                        type="button"
                        onClick={() => setAssetPanel({ scope: "item", kind: "image" })}
                        className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-3 py-1.5 text-sm text-slate-700"
                      >
                        <ImageIcon size={15} /> Item images
                      </button>
                      {canCreate && selectedItem.isActive && (
                        <button
                          type="button"
                          onClick={() => {
                            setConflict(false);
                            setForm({ type: "variant", entity: null });
                          }}
                          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-semibold text-white"
                        >
                          <Plus size={15} /> Add variant
                        </button>
                      )}
                    </div>
                  </div>
                  {loading.variants ? (
                    <div className="flex justify-center py-12 text-slate-500">
                      <Loader2 size={20} className="animate-spin" />
                    </div>
                  ) : variants.items.length === 0 ? (
                    <div className="border-t border-slate-100 px-5 py-12 text-center text-sm text-slate-500">
                      No variants are available.
                    </div>
                  ) : (
                    <div className="overflow-x-auto border-t border-slate-100">
                      <table className="w-full min-w-[1040px] text-left text-sm">
                        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                          <tr>
                            <th className="px-5 py-2.5 font-semibold">Variant</th>
                            <th className="px-4 py-2.5 font-semibold">SKU</th>
                            <th className="px-4 py-2.5 font-semibold">Unit</th>
                            <th className="px-4 py-2.5 font-semibold">Price</th>
                            <th className="px-4 py-2.5 font-semibold">Availability</th>
                            <th className="px-4 py-2.5 font-semibold">Status</th>
                            <th className="px-4 py-2.5 font-semibold">Meta sync</th>
                            <th className="px-4 py-2.5 text-right font-semibold">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {variants.items.map(variant => {
                            const syncStatus = metaSync.items?.find(
                              entry => entry.variantId === variant.id,
                            );
                            const status = syncStatus?.status ??
                              (metaSync.reconnectRequired ? "disconnected" : "not_synced");
                            const statusLabel = {
                              synced: "Synced",
                              failed: "Sync failed",
                              pending: "Sync pending",
                              disconnected: "Reconnect Meta",
                              not_synced: "Not synced",
                            }[status] ?? "Not synced";
                            return (
                            <tr key={variant.id}>
                              <td className="px-5 py-3 font-semibold text-slate-900">
                                <span className="inline-flex items-center gap-2">
                                  {variant.name}
                                  {variant.isDefault && (
                                    <Star size={13} className="text-amber-600" fill="currentColor" />
                                  )}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-slate-600">{variant.sku || "—"}</td>
                              <td className="px-4 py-3 text-slate-600">
                                {variant.unitLabel || variant.unitCode || "—"}
                              </td>
                              <td className="px-4 py-3 text-slate-600">
                                {variant.price === null || variant.price === undefined
                                  ? "Not priced"
                                  : `${variant.currency} ${variant.price}`}
                              </td>
                              <td className="px-4 py-3 capitalize text-slate-600">
                                {String(variant.availabilityStatus).replace(/([A-Z])/g, " $1")}
                              </td>
                              <td className="px-4 py-3"><StatusBadge active={variant.isActive} /></td>
                              <td className="px-4 py-3">
                                <div className="flex min-w-[120px] flex-col items-start gap-1">
                                  <span className={`text-xs font-semibold ${
                                    status === "synced"
                                      ? "text-emerald-700"
                                      : status === "failed"
                                        ? "text-red-700"
                                        : "text-slate-600"
                                  }`}>
                                    {statusLabel}
                                  </span>
                                  {canCreate && !metaSync.reconnectRequired && (
                                    <button
                                      type="button"
                                      onClick={() => syncVariant(variant)}
                                      disabled={Boolean(syncingVariantId)}
                                      className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-800 disabled:opacity-40"
                                    >
                                      <RefreshCw
                                        size={12}
                                        className={syncingVariantId === variant.id ? "animate-spin" : ""}
                                      />
                                      {status === "not_synced" ? "Sync to Meta" : "Sync again"}
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex justify-end gap-1">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAssetPanel({
                                        scope: "variant",
                                        kind: "alias",
                                        variant,
                                      })
                                    }
                                    className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
                                    aria-label={`Manage aliases for ${variant.name}`}
                                    title="Aliases"
                                  >
                                    <Tags size={15} />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAssetPanel({
                                        scope: "variant",
                                        kind: "image",
                                        variant,
                                      })
                                    }
                                    className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
                                    aria-label={`Manage images for ${variant.name}`}
                                    title="Images"
                                  >
                                    <ImageIcon size={15} />
                                  </button>
                                  {canCreate && (
                                    <>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setConflict(false);
                                          setForm({ type: "variant", entity: variant });
                                        }}
                                        className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
                                        aria-label={`Edit ${variant.name}`}
                                        title="Edit"
                                      >
                                        <Edit3 size={15} />
                                      </button>
                                      {!variant.isDefault && variant.isActive && (
                                        <button
                                          type="button"
                                          onClick={() => setDefault("variant", variant)}
                                          className="rounded p-1.5 text-amber-700 hover:bg-amber-50"
                                          aria-label={`Set ${variant.name} as default`}
                                          title="Set default"
                                        >
                                          <Star size={15} />
                                        </button>
                                      )}
                                      {!variant.isActive && (
                                        <button
                                          type="button"
                                          onClick={() => activate("variant", variant)}
                                          className="rounded p-1.5 text-emerald-700 hover:bg-emerald-50"
                                          aria-label={`Activate ${variant.name}`}
                                          title="Activate"
                                        >
                                          <Power size={15} />
                                        </button>
                                      )}
                                    </>
                                  )}
                                  {canDelete && variant.isActive && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openDeactivate("variant", variant)
                                      }
                                      className="rounded p-1.5 text-red-700 hover:bg-red-50"
                                      aria-label={`Deactivate ${variant.name}`}
                                      title="Deactivate"
                                    >
                                      <PowerOff size={15} />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <Pagination
                    pageData={variants}
                    onPage={page => loadVariants(selectedCatalogId, selectedItemId, page)}
                  />
                </section>
              )}
            </div>
          )}
        </div>
      </div>

      {form?.type === "catalog" && (
        <Modal title={form.entity ? "Edit catalogue" : "New catalogue"} onClose={closeForm}>
          <CatalogForm
            initial={form.entity}
            conflict={conflict}
            onReload={reloadWorkspace}
            onCancel={closeForm}
            onSubmit={saveCatalog}
            saving={saving}
          />
        </Modal>
      )}
      {form?.type === "item" && (
        <Modal title={form.entity ? "Edit item" : "New item"} onClose={closeForm} width="max-w-3xl">
          <ItemForm
            initial={form.entity}
            categories={categories}
            categoriesLoading={loading.categories}
            categoriesError={categoryError}
            defaultCurrency={selectedCatalog?.defaultCurrency}
            conflict={conflict}
            onReload={reloadWorkspace}
            onCancel={closeForm}
            onSubmit={saveItem}
            saving={saving}
          />
        </Modal>
      )}
      {form?.type === "variant" && (
        <Modal
          title={form.entity ? "Edit variant" : "New variant"}
          onClose={closeForm}
          width="max-w-3xl"
        >
          <VariantForm
            initial={form.entity}
            defaultCurrency={selectedCatalog?.defaultCurrency}
            conflict={conflict}
            onReload={reloadWorkspace}
            onCancel={closeForm}
            onSubmit={saveVariant}
            saving={saving}
          />
        </Modal>
      )}

      {assetPanel && selectedCatalogId && selectedItemId && (
        <CatalogAssetsManager
          panel={assetPanel}
          catalogId={selectedCatalogId}
          itemId={selectedItemId}
          canCreate={canCreate}
          canDelete={canDelete}
          onClose={() => setAssetPanel(null)}
        />
      )}

      {categoryPanel && selectedCatalog && (
        <CategoryManager
          catalog={selectedCatalog}
          categories={categories}
          loading={loading.categories}
          loadError={categoryError}
          canCreate={canCreate}
          canDelete={canDelete}
          onRefresh={() => loadCategories(selectedCatalog.id)}
          onClose={() => setCategoryPanel(false)}
        />
      )}

      {confirm && (
        <Modal
          title={`Deactivate ${confirm.kind}?`}
          onClose={() => setConfirm(null)}
          width="max-w-md"
          footer={
            <>
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  saving ||
                  (confirm.kind === "catalog" &&
                    confirm.entity.isDefault &&
                    !confirm.replacementId) ||
                  (confirm.kind === "variant" &&
                    confirm.entity.isDefault &&
                    !confirm.replacementId)
                }
                onClick={confirmDeactivate}
                className="inline-flex items-center gap-2 rounded-md bg-red-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {saving && <Loader2 size={15} className="animate-spin" />}
                Deactivate
              </button>
            </>
          }
        >
          <div className="flex gap-3 text-sm text-slate-700">
            <AlertTriangle className="shrink-0 text-red-600" size={20} />
            <p>
              This will make <strong>{confirm.entity.name}</strong> unavailable for
              active catalogue operations.
            </p>
          </div>
          {confirm.kind === "catalog" && confirm.entity.isDefault && (
            <label className={`${labelClass} mt-4 block`}>
              Replacement default catalogue
              {confirm.optionsError && (
                <span className="mt-1 block text-xs font-normal text-red-700">
                  {confirm.optionsError}
                </span>
              )}
              <select
                className={fieldClass}
                value={confirm.replacementId}
                disabled={confirm.loadingOptions}
                onChange={event =>
                  setConfirm(current => ({ ...current, replacementId: event.target.value }))
                }
              >
                <option value="">Select an active catalogue</option>
                {confirm.options.map(entry => (
                  <option key={entry.id} value={entry.id}>{entry.name}</option>
                ))}
              </select>
              {confirm.loadingOptions && (
                <span className="mt-1 block text-xs font-normal text-slate-500">
                  Loading eligible catalogues...
                </span>
              )}
            </label>
          )}
          {confirm.kind === "variant" && confirm.entity.isDefault && (
            <label className={`${labelClass} mt-4 block`}>
              Replacement default variant
              {confirm.optionsError && (
                <span className="mt-1 block text-xs font-normal text-red-700">
                  {confirm.optionsError}
                </span>
              )}
              <select
                className={fieldClass}
                value={confirm.replacementId}
                disabled={confirm.loadingOptions}
                onChange={event =>
                  setConfirm(current => ({ ...current, replacementId: event.target.value }))
                }
              >
                <option value="">Select an active variant</option>
                {confirm.options.map(entry => (
                  <option key={entry.id} value={entry.id}>{entry.name}</option>
                ))}
              </select>
              {confirm.loadingOptions && (
                <span className="mt-1 block text-xs font-normal text-slate-500">
                  Loading eligible variants...
                </span>
              )}
            </label>
          )}
        </Modal>
      )}
    </main>
  );
}
